import React, {useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {AgentBasicDto} from "../../dto/AgentBasicDto";
import {
  deletePlaybookExample,
  getAgentBasic,
  getPlaybookExamples, postPlaybookExample,
  putAgentBasic,
  putPlaybookExample,
  sendQuestion
} from "../../services/DataService";
import axios from "axios";
import {useAuth} from "../../context/useAuth";
import {setSnackbarOpen} from "../../store/slices/snackbar.slice";
import {PlaybookExampleDto} from "../../dto/PlaybookExampleDto";
import {ConfirmSaveDialog} from "../../components/confirmSaveDialog/ConfirmSaveDialog";
import {setCancel, setConfirm, setDiscard, setUnsavedChanges} from "../../store/slices/changes.slice";
import {updateAgent} from "../../store/slices/agents.slice";
import {DialogStep} from "../../models/DialogStep";
import {MaiSplitPage} from "../../components/splitPage/MaiSplitPage";
import AgentForm from "../../components/agentForm/AgentForm";
import ExampleTable from "../../components/exampleTable/ExampleTable";
import ExampleEditor from "../../components/exampleEditor/ExampleEditor";
import {AgentPreview} from "../../components/agentPreview/AgentPreview";
import {Question} from "../../models/Question";
import {handleApiCall} from "../../utils/handleApiCall";
import ToggleTabs from "../../components/tabs/ToggleTabs";
import {setActionButton} from "../../store/slices/layout.slice";
import {setActionButtonCallback} from "../../utils/actionButtonHandler";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

enum tabs {
  Grundlagen,
  Beispiele,
}

const tabLabels = ['Grundlagen', 'Beispiele'];

function CustomTabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
      <div
          role="tabpanel"
          hidden={value !== index}
          id={`simple-tabpanel-${index}`}
          aria-labelledby={`simple-tab-${index}`}
          {...other}
      >
        {value === index && <Box>{children}</Box>}
      </div>
  );
}

export function AgentPage() {
  const {token, logout} = useAuth();
  const dispatch = useDispatch();
  const {selectedAgent} = useSelector((state: RootState) => state.agents);
  const {unsavedChanges, confirm, discard, cancel} = useSelector((state: RootState) => state.changes);
  const [agentBasic, setAgentBasic] = useState<AgentBasicDto | null>();
  const [value, setValue] = useState(0);
  const [newValue, setNewValue] = useState(0);
  const [agentName, setAgentName] = useState('');
  const [playbookName, setPlaybookName] = useState('');
  const [goal, setGoal] = useState('');
  const [instructions, setInstructions] = useState('');
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [examples, setExamples] = useState<PlaybookExampleDto[]>([]);
  const [selectedExampleId, setSelectedExampleId] = useState<string | null>(null);
  const [exampleName, setExampleName] = useState('');
  const [exampleDialogSteps, setExampleDialogSteps] = useState<DialogStep[]>([]);
  const [inputErrors, setInputErrors] = useState<Record<number, {
    userRequest?: boolean;
    agentResponse?: boolean
  }>>({});
  const [exampleNameError, setExampleNameError] = React.useState(false);
  const [exampleNameErrorMessage, setExampleNameErrorMessage] = React.useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [isWaitingForResponse, setWaitForResponse] = useState(false);

  const getSelectedExample = () => {
    return examples.find(example => example.gcpName === selectedExampleId);
  }

  async function getAgentBasics(selectedAgentId: string, token: string) {
    await handleApiCall(() =>
            getAgentBasic(selectedAgentId, token),
        {
          onSuccess: (response) => {
            setAgentBasic(response.data);
          },
          errorMessage: "Fehler beim Laden der Agenteninformationen.",
          logout,
          dispatch,
        }
    );
  }

  async function putAgentBasics(selectedAgentId: string, token: string) {
    const agentBasicDto: AgentBasicDto = {
      gcpAgentId: agentBasic?.gcpAgentId ?? '',
      projectId: agentBasic?.projectId ?? '',
      location: agentBasic?.location ?? '',
      clientName: agentBasic?.clientName ?? '',
      agentDisplayName: agentName,
      gcpPlaybookId: agentBasic?.gcpPlaybookId ?? '',
      playbookDisplayName: playbookName,
      messengerEnabled: agentBasic?.messengerEnabled ?? false,
      goal,
      instructions,
    };

    await handleApiCall(() =>
            putAgentBasic(selectedAgentId, agentBasicDto, token),
        {
          successMessage: 'Speichern erfolgreich!',
          errorMessage: 'Speichern fehlgeschlagen.',
          onSuccess: (response) => {
            setAgentBasic(response.data);
            dispatch(updateAgent(response.data));
            dispatch(setUnsavedChanges(false));
          },
          logout,
          dispatch,
        }
    );
  }

  async function setPlaybookExamples(selectedAgentId: string, playbookId: string, token: string) {
    setLoadingExamples(true);
    await handleApiCall(() =>
            getPlaybookExamples(selectedAgentId, playbookId, token),
        {
          onSuccess: (response) => {
            setExamples(response.data);
            setLoadingExamples(false);
          },
          logout,
          dispatch,
        }
    );
  }

  async function savePlaybookExample(selectedAgentId: string, selectedPlaybookId: string, token: string, exampleId: string) {
    const selectedExample = getSelectedExample();
    const playbookExample: PlaybookExampleDto = {
      gcpName: selectedExample?.gcpName,
      displayName: exampleName,
      dialogSteps: exampleDialogSteps,
      languageCode: selectedExample?.languageCode,
      tokenCount: selectedExample?.tokenCount,
      playbookOutput: selectedExample?.playbookOutput
    };

    await handleApiCall(() =>
            putPlaybookExample(selectedAgentId, selectedPlaybookId, token, exampleId, playbookExample),
        {
          successMessage: 'Speichern erfolgreich!',
          errorMessage: 'Speichern fehlgeschlagen.',
          onSuccess: async (response) => {
            dispatch(setUnsavedChanges(false));
            await setPlaybookExamples(selectedAgentId, selectedPlaybookId, token);
            setSelectedExampleId(response.data.gcpName);
          },
          logout,
          dispatch,
        }
    );
  }

  async function addPlaybookExample(selectedAgentId: string, selectedPlaybookId: string, token: string) {
    const playbookExample: PlaybookExampleDto = {
      displayName: exampleName,
      dialogSteps: exampleDialogSteps,
    };

    await handleApiCall(() =>
            postPlaybookExample(selectedAgentId, selectedPlaybookId, token, playbookExample),
        {
          successMessage: 'Speichern erfolgreich!',
          errorMessage: 'Speichern fehlgeschlagen.',
          onSuccess: async (response) => {
            dispatch(setUnsavedChanges(false));
            await setPlaybookExamples(selectedAgentId, selectedPlaybookId, token);
            setSelectedExampleId(response.data.gcpName);
          },
          logout,
          dispatch,
        }
    );
  }

  async function deleteExample(selectedAgentId: string, selectedPlaybookId: string, token: string, exampleId: string) {
    await handleApiCall(() =>
            deletePlaybookExample(selectedAgentId, selectedPlaybookId, token, exampleId),
        {
          successMessage: 'Löschen erfolgreich!',
          errorMessage: 'Löschen fehlgeschlagen.',
          onSuccess: async () => {
            await setPlaybookExamples(selectedAgentId, selectedPlaybookId, token);
            setSelectedExampleId(null);
          },
          logout,
          dispatch,
        }
    );
  }

  const updateValue = () => {
    if (newValue !== value) {
      setValue(newValue);
      if (newValue === tabs.Beispiele && selectedAgent && agentBasic && token) {
        setPlaybookExamples(selectedAgent.gcpAgentId, agentBasic?.gcpPlaybookId, token)
      }
    }
  }

  const resetFormValues = () => {
    if (agentBasic) {
      setAgentName(agentBasic.agentDisplayName);
      setPlaybookName(agentBasic.playbookDisplayName);
      setGoal(agentBasic.goal);
      setInstructions(agentBasic.instructions);
    }
  }

  const resetExampleValues = () => {
    const selectedExample = getSelectedExample();
    if (selectedExample) {
      setExampleName(selectedExample.displayName);
      setExampleDialogSteps(selectedExample.dialogSteps);
    } else {
      setExampleName("");
      setExampleDialogSteps([]);
    }
  }
  useEffect(() => {
    if (selectedAgent && token) {
      getAgentBasics(selectedAgent.gcpAgentId, token);
    }
  }, [selectedAgent, token]);

  useEffect(() => {
    resetFormValues();
    setSelectedExampleId(null);
    if (value === tabs.Beispiele && selectedAgent && agentBasic && token) {
      setPlaybookExamples(selectedAgent.gcpAgentId, agentBasic?.gcpPlaybookId, token)
    }
  }, [agentBasic]);

  useEffect(() => {
    if (isCopying) return;

    if (!selectedExampleId) {
      return;
    }

    const selectedExample = getSelectedExample();
    if (selectedExample) {
      setExampleName(selectedExample.displayName);
      const clonedSteps = selectedExample.dialogSteps.map(step => ({
        userRequest: step.userRequest ?? "",
        agentResponse: step.agentResponse ?? ""
      }));
      setExampleDialogSteps(clonedSteps);
    }
  }, [selectedExampleId, isCopying]);

  const handleSaveExample = () => {
    if (selectedAgent && agentBasic && token && validateExample()) {
      if (selectedExampleId) {
        savePlaybookExample(selectedAgent?.gcpAgentId, agentBasic?.gcpPlaybookId, token, selectedExampleId);
      } else {
        addPlaybookExample(selectedAgent?.gcpAgentId, agentBasic?.gcpPlaybookId, token);
      }
    }
  };

  const handleSaveAgent = () => {
    if (selectedAgent && token) {
      putAgentBasics(selectedAgent.gcpAgentId, token);
    }
  };

  const handleSaveExampleRef = useRef(handleSaveExample);
  const handleSaveAgentRef = useRef(handleSaveAgent);

  useEffect(() => {
    handleSaveExampleRef.current = handleSaveExample;
  }, [handleSaveExample]);

  useEffect(() => {
    handleSaveAgentRef.current = handleSaveAgent;
  }, [handleSaveAgent]);

  useEffect(() => {
    dispatch(setActionButton({
      label: 'Speichern',
      disabled: !unsavedChanges,
      hidden: false,
    }));

    if (value === tabs.Grundlagen) {
      setActionButtonCallback(() => handleSaveAgentRef.current());
    } else if (value === tabs.Beispiele) {
      setActionButtonCallback(() => handleSaveExampleRef.current());
    }

    return () => {
      dispatch(setActionButton(null));
      setActionButtonCallback(null);
    };
  }, [value, unsavedChanges]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setNewValue(newValue);
    if (unsavedChanges) {
      setOpen(true);
    } else {
      setValue(newValue);
      if (newValue === tabs.Beispiele && selectedAgent && agentBasic && token) {
        setPlaybookExamples(selectedAgent.gcpAgentId, agentBasic?.gcpPlaybookId, token)
      }
    }
  };

  const handleDialogSave = () => {
    setOpen(false);
    if (value === tabs.Grundlagen) {
      handleSaveAgent();
    } else if (value === tabs.Beispiele) {
      handleSaveExample();
    }
    dispatch(setUnsavedChanges(false));
    updateValue();
  }

  const handleClose = () => {
    setOpen(false);
  };

  const handleDiscard = () => {
    setOpen(false);
    if (value === tabs.Grundlagen) {
      resetFormValues();
    } else if (value === tabs.Beispiele) {
      resetExampleValues();
    }
    dispatch(setUnsavedChanges(false));
    updateValue();
  };

  const handleAddExample = () => {
    setSelectedExampleId(null);
    setExampleName("");
    setExampleDialogSteps([{userRequest: "", agentResponse: ""}]);
    dispatch(setUnsavedChanges(true));
  };

  const handleAddDialogStep = () => {
    setExampleDialogSteps(prevItems => [
      ...prevItems,
      {}
    ]);
    dispatch(setUnsavedChanges(true));
  }

  const handleDeleteDialogStep = (indexToRemove: number) => {
    setExampleDialogSteps(prevItems =>
        prevItems.filter((_, index) => index !== indexToRemove)
    );
    dispatch(setUnsavedChanges(true));
  };

  useEffect(() => {
    if (confirm) {
      dispatch(setConfirm(false));
      handleDialogSave();
    }
  }, [confirm]);

  useEffect(() => {
    if (discard) {
      dispatch(setDiscard(false));
      handleDiscard();
    }
  }, [discard]);

  useEffect(() => {
    if (cancel) {
      dispatch(setCancel(false));
      handleClose();
    }
  }, [cancel]);

  const handleSend = async () => {
    if (selectedAgent && token) {
      setWaitForResponse(true);
      await sendQuestion(selectedAgent.gcpAgentId, currentQuestion, token, sessionId)
      .then((response) => {
        setSessionId(response.data.sessionId);
        const newQuestion: Question = {
          question: response.data.question,
          answer: response.data.answer,
          sessionId: response.data.sessionId,
        };
        setQuestions((prev) => [...prev, newQuestion]);
        setWaitForResponse(false);
      })
      .catch(function (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          logout();
        } else {
          dispatch(setSnackbarOpen({snackbarMessage: "Senden der Frage fehlgeschlagen.", snackbarSeverity: 'error'}));
        }
        setWaitForResponse(false);
      })
    }
    setCurrentQuestion('');
  }

  const handleExampleClick = (
      exampleId?: string,
  ) => {
    setSelectedExampleId(exampleId ?? null);
  };

  const handleDeleteExample = (
      exampleId?: string,
  ) => {
    if (selectedAgent && agentBasic && token && exampleId) {
      deleteExample(selectedAgent.gcpAgentId, agentBasic?.gcpPlaybookId, token, exampleId);
    }
  };

  const handleCopyExample = (exampleId?: string) => {
    if (!exampleId || !selectedAgent || !agentBasic || !token) return;

    const originalExample = examples.find(e => e.gcpName === exampleId);
    if (!originalExample) return;

    setIsCopying(true);

    const copiedDialogSteps = originalExample.dialogSteps.map(step => ({
      userRequest: step.userRequest ?? "",
      agentResponse: step.agentResponse ?? ""
    }));

    setExampleName(`Kopie von ${originalExample.displayName}`);
    setExampleDialogSteps(copiedDialogSteps);
    setSelectedExampleId(null);

    dispatch(setUnsavedChanges(true));
    setTimeout(() => setIsCopying(false), 0);
  };

  const handleUpdateDialogStep = (
      index: number,
      field: 'userRequest' | 'agentResponse',
      value: string
  ) => {
    setExampleDialogSteps(prev =>
        prev.map((step, i) =>
            i === index ? {...step, [field]: value} : step
        )
    );
    dispatch(setUnsavedChanges(true));
  };

  const validateExample = (): boolean => {
    let isValid = validateDialogSteps();
    if (!exampleName.trim()) {
      setExampleNameError(true);
      setExampleNameErrorMessage('Bitte geben Sie einen Namen ein.');
      isValid = false;
    } else {
      setExampleNameError(false);
      setExampleNameErrorMessage('');
    }
    if (!isValid) {
      dispatch(setSnackbarOpen({snackbarMessage: "Speichern fehlgeschlagen.", snackbarSeverity: 'error'}));
    }
    return isValid;
  }

  const validateDialogSteps = (): boolean => {
    const errors: Record<number, { userRequest?: boolean; agentResponse?: boolean }> = {};

    exampleDialogSteps.forEach((step, index) => {
      const userEmpty = !step.userRequest?.trim();
      const agentEmpty = !step.agentResponse?.trim();

      if (userEmpty || agentEmpty) {
        errors[index] = {};
        if (userEmpty) errors[index].userRequest = true;
        if (agentEmpty) errors[index].agentResponse = true;
      }
    });

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const leftPage = (
      <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
      >
        <Box sx={{flexShrink: 0}}>
          <ToggleTabs
              value={value}
              onChange={handleChange}
              tabLabels={tabLabels}
          />
        </Box>

        <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
        >
          <CustomTabPanel value={value} index={tabs.Grundlagen}>
            <AgentForm
                agentName={agentName}
                playbookName={playbookName}
                goal={goal}
                instructions={instructions}
                setAgentName={setAgentName}
                setPlaybookName={setPlaybookName}
                setGoal={setGoal}
                setInstructions={setInstructions}
                onChangeDetected={() => dispatch(setUnsavedChanges(true))}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={tabs.Beispiele}>
            <ExampleTable
                examples={examples}
                selectedExampleId={selectedExampleId}
                loading={loadingExamples}
                onSelect={handleExampleClick}
                onCopy={handleCopyExample}
                onDelete={handleDeleteExample}
                onAddExample={handleAddExample}
            />
          </CustomTabPanel>
        </Box>

        <ConfirmSaveDialog
            open={open}
            onConfirm={handleDialogSave}
            onDiscard={handleDiscard}
            onCancel={handleClose}
        />
      </Box>
  );

  const rightPage = (
      <Box display="flex" flexDirection="column" height="100%">
        {value === tabs.Beispiele && exampleDialogSteps.length > 0 && (
            <ExampleEditor
                exampleName={exampleName}
                exampleNameError={exampleNameError}
                exampleNameErrorMessage={exampleNameErrorMessage}
                dialogSteps={exampleDialogSteps}
                inputErrors={inputErrors}
                onChangeExampleName={(val) => {
                  setExampleName(val);
                  dispatch(setUnsavedChanges(true));
                }}
                onAddDialogStep={handleAddDialogStep}
                onDeleteDialogStep={handleDeleteDialogStep}
                onUpdateDialogStep={handleUpdateDialogStep}
            />
        )}

        {value !== tabs.Beispiele && (
            <AgentPreview
                questions={questions}
                currentQuestion={currentQuestion}
                onChangeQuestion={setCurrentQuestion}
                onSend={handleSend}
                isWaitingForResponse={isWaitingForResponse}
            />
        )}
      </Box>
  );

  return (
      <MaiSplitPage
          childrenLeft={leftPage}
          childrenRight={rightPage}
      />
  )
}